import React, { useEffect } from 'react';
import { COMPANY } from '../lib/company';

const LAST_UPDATED = 'August 12, 2026';

const PrivacyPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Thornless Data Management';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Privacy policy for Thornless Data Management, a Kuiken Technologies LLC service.');
  }, []);

  const section = (title: string, children: React.ReactNode) => (
    <div style={{ marginBottom: '2.5rem' }}>
      <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text)', marginBottom: '0.75rem' }}>{title}</h2>
      <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)' }}>{children}</div>
    </div>
  );

  const link = (href: string, label: string, external = false) => (
    <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} style={{ color: 'var(--color-primary)' }}>{label}</a>
  );

  return (
    <main>
      <section style={{ backgroundColor: 'var(--color-primary)', color: '#ffffff', padding: '4rem 0 3rem' }}>
        <div className="layout-container">
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '0.75rem' }}>Legal</p>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'var(--font-weight-extrabold)', lineHeight: 'var(--line-height-tight)', color: '#ffffff', marginBottom: '1rem' }}>Privacy Policy</h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 'var(--font-size-base)' }}>Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="layout-container section-padding">
        <div style={{ maxWidth: '760px' }}>

          {section('1. Overview', (
            <p>
              {COMPANY.tradeName} is a service of {COMPANY.legalName} ("{COMPANY.tradeName}," "Thornless," "we," "us," or
              "our"). This Privacy Policy explains what information we collect through our website ({COMPANY.domain}) and in
              the course of client engagements, how we use and share it, and the choices and rights available to you. It
              covers this website and our direct communications with you; it does not cover third-party sites we link to. By
              using our website or engaging our services, you agree to the practices described in this policy.
            </p>
          ))}

          {section('2. Information We Collect', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>We collect user data in the following categories:</p>
              <ul style={{ paddingLeft: '1.5rem', marginBottom: '0.75rem' }}>
                <li style={{ marginBottom: '0.4rem' }}>
                  <strong>Contact and inquiry information.</strong> Name, email address, phone number (optional), company,
                  area of interest, and message content submitted through our contact form.
                </li>
                <li style={{ marginBottom: '0.4rem' }}>
                  <strong>Business and engagement information.</strong> Business details, project requirements, and — where a
                  client engagement grants us access to systems or data to deliver a service — the client data necessary to
                  perform that work, handled under §6, "Client Data and Confidentiality."
                </li>
                <li style={{ marginBottom: '0.4rem' }}>
                  <strong>Account information.</strong> This website does not have user accounts or logins, so we do not
                  collect or store account credentials or passwords for site visitors.
                </li>
                <li style={{ marginBottom: '0.4rem' }}>
                  <strong>Usage and analytics data.</strong> If you accept analytics cookies through our cookie banner, we
                  collect aggregate information about how you interact with our site — pages viewed, time on page, and
                  referring URLs — through Google Analytics. See §10, "Cookies and Similar Technologies."
                </li>
                <li style={{ marginBottom: '0.4rem' }}>
                  <strong>Payment information.</strong> This website does not process payments and does not collect payment
                  card or bank account information. Fees for client engagements are invoiced and paid through channels
                  agreed in the applicable Statement of Work, outside of this website.
                </li>
                <li style={{ marginBottom: '0.4rem' }}>
                  <strong>Device and session data.</strong> Our hosting and security providers automatically log technical
                  information for every request, including IP address, browser type and version, operating system, referring
                  page, and request timestamps. This logging happens regardless of your cookie choice, as described in §10.
                </li>
              </ul>
            </>
          ))}

          {section('3. How We Use Your Information', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>We use the information we collect to:</p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.4rem' }}>Respond to your inquiries and provide the services you request</li>
                <li style={{ marginBottom: '0.4rem' }}>Communicate about project scope, progress, and deliverables</li>
                <li style={{ marginBottom: '0.4rem' }}>Operate, secure, and improve our website</li>
                <li style={{ marginBottom: '0.4rem' }}>Understand aggregate site usage, where you have consented to analytics cookies</li>
                <li style={{ marginBottom: '0.4rem' }}>Comply with applicable legal obligations</li>
              </ul>
              <p style={{ marginTop: '0.75rem', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text)' }}>
                We do not sell your personal information.
              </p>
            </>
          ))}

          {section('4. Use of Artificial Intelligence', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                Thornless designs and builds AI-based automation (for example, agentic workflows and document classification)
                as part of the data engineering services we deliver to clients. Where an engagement involves AI tooling, we
                use third-party AI providers — currently OpenAI, Anthropic, and cloud-hosted models available through
                Microsoft Azure OpenAI Service and AWS Bedrock — to build and run that tooling. Any client data submitted to
                these providers in the course of an engagement is governed by the confidentiality terms of that engagement's
                Statement of Work, not by this website's ordinary data flows.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                We do not use website visitor data (contact form submissions or site analytics) as input to AI systems, and
                we do not use AI to make automated decisions about you — such as evaluating an inquiry or determining
                eligibility for a service — that would have a legal or similarly significant effect without a human
                reviewing the outcome. AI-assisted tools may be used internally to draft or accelerate our own work (for
                example, parts of this website's content and code), but a person reviews and is responsible for what we
                publish and deliver.
              </p>
            </>
          ))}

          {section('5. Data Sharing and Third Parties', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                We do not sell, trade, or rent your personal information. We share information only with the service
                providers ("processors") below, each solely to operate this website or deliver our services, and only to
                the extent necessary for that purpose:
              </p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.6rem' }}>
                  <strong>Supabase</strong> (database hosting) — receives and stores contact form submissions so we can
                  respond to and track inquiries. {link('https://supabase.com/privacy', 'Privacy policy', true)}.
                </li>
                <li style={{ marginBottom: '0.6rem' }}>
                  <strong>Resend</strong> (transactional email) — receives contact form content to deliver it to our inbox as
                  an email notification. {link('https://resend.com/legal/privacy-policy', 'Privacy policy', true)}.
                </li>
                <li style={{ marginBottom: '0.6rem' }}>
                  <strong>Cloudflare</strong> (hosting, CDN, and security) — receives standard request/device data (see §2)
                  for every visitor as part of serving this website and protecting it from abuse.{' '}
                  {link('https://www.cloudflare.com/privacypolicy/', 'Privacy policy', true)}.
                </li>
                <li style={{ marginBottom: '0.6rem' }}>
                  <strong>Google Analytics</strong> (site analytics) — receives usage data described in §10, and only after
                  you accept analytics cookies. {link('https://policies.google.com/privacy', 'Privacy policy', true)}.
                </li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>
                We may also disclose information where required by law, to protect our legal rights, or in connection with a
                merger, acquisition, or sale of assets, subject to this policy continuing to govern any personal information
                involved.
              </p>
            </>
          ))}

          {section('6. Client Data and Confidentiality', (
            <p>Any data, credentials, or business information shared with us during a client engagement is treated as strictly confidential. We access client systems and data solely for the purpose of delivering agreed services. We do not retain, analyze, or use client data beyond the scope of the engagement without explicit written consent.</p>
          ))}

          {section('7. Data Retention', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>We keep personal information only as long as reasonably necessary for the purposes described in this policy:</p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.4rem' }}><strong>Contact form submissions</strong> that do not lead to an engagement: retained up to 24 months from submission, then deleted or anonymized.</li>
                <li style={{ marginBottom: '0.4rem' }}><strong>Client and engagement records</strong> (correspondence, invoicing, signed agreements): retained for the duration of the business relationship plus 7 years, to meet standard contract and tax recordkeeping obligations.</li>
                <li style={{ marginBottom: '0.4rem' }}><strong>Analytics data</strong>: retained under Google Analytics' own retention settings, currently configured to expire automatically rather than persist indefinitely.</li>
                <li style={{ marginBottom: '0.4rem' }}><strong>Server and security logs</strong> (Cloudflare): retained on a rolling basis, typically 30 days, per Cloudflare's standard logging practices.</li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>You may request earlier deletion of your personal information at any time — see §9, "Your Rights."</p>
            </>
          ))}

          {section('8. Data Breach Notification', (
            <p>
              If we become aware of a breach of security compromising your personal information, we will notify affected
              individuals and, where required, regulators without unreasonable delay and consistent with applicable law,
              including the Illinois Personal Information Protection Act (815 ILCS 530) and, where applicable, other state
              and federal breach notification requirements.
            </p>
          ))}

          {section('9. Security', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>We implement technical and organizational measures appropriate to the data we handle, including:</p>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li style={{ marginBottom: '0.4rem' }}>Encryption of data in transit over HTTPS/TLS between your browser and our servers</li>
                <li style={{ marginBottom: '0.4rem' }}>No user account system on this website, so there are no visitor passwords for us to store</li>
                <li style={{ marginBottom: '0.4rem' }}>Database access restricted to a server-side service role — client-side code cannot read or write our database directly, and row-level security is enabled on all tables</li>
                <li style={{ marginBottom: '0.4rem' }}>Access to client systems and engagement data limited to the personnel performing that engagement</li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>No method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.</p>
            </>
          ))}

          {section('10. Your Rights', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                You may request access to, correction of, or deletion of your personal information at any time by contacting
                us at the address in §14. We will respond within 30 days.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                <strong>If you are in the European Economic Area or United Kingdom (GDPR):</strong> you have the right to
                access, correct, delete, restrict, or port your personal information, and to object to certain processing.
                You may also lodge a complaint with your local data protection authority.
              </p>
              <p>
                <strong>If you are a California resident (CCPA/CPRA):</strong> you have the right to know what personal
                information we collect, to request deletion, and to non-discrimination for exercising these rights. As
                stated above, we do not sell your personal information.
              </p>
            </>
          ))}

          {section('11. Cookies and Similar Technologies', (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                On your first visit, a banner asks you to choose "Accept All" or "Essential Only." We honor that choice in
                the browser itself: analytics scripts are not loaded until you click "Accept All," so declining actually
                prevents those cookies from being set, not just hides a notice.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                <strong>Strictly necessary.</strong> Cloudflare's security cookies (such as bot-mitigation cookies) may be
                set regardless of your choice — they don't require consent because they exist to keep the site secure and
                available, not to track you.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                <strong>Analytics (only after "Accept All").</strong> Google Analytics cookies, used to understand aggregate
                site usage. You can opt out at any time by clearing your browser's local storage for this site (which
                resets your choice and re-shows the banner) or by installing the{' '}
                {link('https://tools.google.com/dlpage/gaoptout', 'Google Analytics Opt-out Browser Add-on', true)}.
              </p>
              <p>We do not use advertising or cross-site tracking cookies.</p>
            </>
          ))}

          {section("12. Children's Privacy", (
            <p>
              Our website and services are not directed to children under 13, and we do not knowingly collect personal
              information from children under 13. If you believe a child has provided us with personal information, contact
              us at the address in §14 and we will delete it.
            </p>
          ))}

          {section('13. International Users', (
            <p>
              Our website and service providers operate in the United States, and information we collect is processed and
              stored in the United States. If you access our website from outside the United States, your information will
              be transferred to, stored, and processed in the United States, where data protection laws may differ from
              those in your jurisdiction.
            </p>
          ))}

          {section('14. Changes to This Policy', (
            <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Continued use of our website after any changes constitutes acceptance of the updated policy.</p>
          ))}

          {section('15. Contact Us', (
            <>
              <p style={{ marginBottom: '0.5rem' }}>
                If you have questions about this Privacy Policy or how we handle your information, contact us:
              </p>
              <p style={{ margin: 0 }}>{COMPANY.legalName} ({COMPANY.tradeName})</p>
              {COMPANY.addressLines.map((line) => <p key={line} style={{ margin: 0 }}>{line}</p>)}
              <p style={{ marginTop: '0.5rem' }}>Email: {link(`mailto:${COMPANY.email}`, COMPANY.email)}</p>
              <p>Or via {link('/contact', 'our contact page')}.</p>
            </>
          ))}

        </div>
      </section>
    </main>
  );
};

export default PrivacyPage;
